import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import PaginationControls from "aldoo-ra/Pagination";
import Typography from "aldoo-ra/Typography";
import Input from "aldoo-ra/Input";
import Button from "aldoo-ra/Button";
import Checkbox from "aldoo-ra/Checkbox";
import { VariantReader } from "aldoo-ra/VariantReader";
import { MagnifyingGlassIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences";
import useDataController from "./controller";
import { twMerge } from "tailwind-merge";

const extractValue = (object, path) => {
  const parts = path.split(".");
  let value = object;
  for (let i = 0; i < parts.length; i++) {
    value = value[parts[i]];
    if (value === undefined) break;
  }
  
  // Handle objects that might contain displayable properties
  if (value && typeof value === 'object' && value !== null && !Array.isArray(value)) {
    // If it's a CMS option object, return the renderable part
    if ('value' in value && 'name' in value) {
      return value.name || value.value || JSON.stringify(value);
    }
    // For other objects, try common display properties
    if (value.name || value.label || value.title || value.id) {
      return value.name || value.label || value.title || value.id;
    }
    
    return JSON.stringify(value);
  }
  
  return value;
};

const DataTable = ({
  variant = "default",
  styleOverrides,
  data,
  apiMethod = "",
  apiDataField = "data",
  renders,
  renderRow, // Custom row renderer function
  title,
  noData,
  preferredHeight = "100%",
  searchField,
  headerControls,
  filterControl, // Filter button to display next to search input
  tableHeader, // New prop for content below search/title
  tableFooter, // New prop for content below table but above pagination
  searchObject = {},
  requestFields,
  refreshKey,
  sort,
  showFooter = true,
  minSearchTriggerTime = 250,
  onDataLoaded,
  onDataLoadingElement,
  showSearch = true,
  itemHeight,
  itemsPerPage: itemsPerPageProp = 10,
  localUserPreferencesKey = null, // Key for storing user preferences
  foldingLogic,
  initialSearchTerm = "",
  onSearchTermChange,
  onRowClick,
  // Selection props
  enableSelection = false,
  selectedItems = undefined,
  selectedData = undefined,
  onSelectionChange = undefined,
  bulkActions = [],
  itemIdField = "_id",
  selectionColumnPosition = "first",
}) => {
  // Get styles from variant
  const styles = VariantReader.useVariant({
    componentName: "DataTable",
    variant,
    styleOverrides,
  });

  // Generate child preference keys based on base key
  const preferenceKeys = useMemo(() => {
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      return {
        itemsPerPage: `${localUserPreferencesKey}.itemsPerPage`,
        searchTerm: `${localUserPreferencesKey}.searchTerm`,
        sortBy: `${localUserPreferencesKey}.sortBy`,
        page: `${localUserPreferencesKey}.page`,
      };
    }
    return {
      itemsPerPage: null,
      searchTerm: null,
      sortBy: null,
      page: null,
    };
  }, [localUserPreferencesKey]);

  // State management
  const [page, _setPage] = useState(() => {
    if (preferenceKeys?.page) {
      try {
        const saved = LocalUserPreferences.get(preferenceKeys.page);
        if (typeof saved === "number" && saved >= 1) return saved;
      } catch {}
    }
    return 1;
  });
  const setPage = (next) => {
    _setPage(next);
    if (preferenceKeys?.page) {
      try { LocalUserPreferences.set(preferenceKeys.page, next); } catch {}
    }
  };
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    // Try to load from user preferences first, then fall back to prop
    if (preferenceKeys.itemsPerPage) {
      try {
        const savedValue = LocalUserPreferences.get(
          preferenceKeys.itemsPerPage
        );
        if (savedValue !== undefined && typeof savedValue === "number") {
          return savedValue;
        }
      } catch (error) {
        console.warn("Failed to load itemsPerPage preference:", error);
      }
    }
    return itemsPerPageProp;
  });

  // Selection state (uncontrolled mode)
  const [internalSelectedItems, setInternalSelectedItems] = useState([]);
  const [internalSelectedData, setInternalSelectedData] = useState([]);

  // Determine if controlled or uncontrolled
  const isControlled = selectedItems !== undefined && onSelectionChange !== undefined;
  const activeSelectedItems = isControlled ? selectedItems : internalSelectedItems;
  const activeSelectedData = isControlled ? selectedData || [] : internalSelectedData;
  const [isScrollLocked, setIsScrollLocked] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [activeSortField, setActiveSortField] = useState(sort?.field || null);
  const [sortDirection, setSortDirection] = useState(sort?.order || "asc");
  const [activeSort, setActiveSort] = useState(sort || null);
  const [totalItems, setTotalItems] = useState(0);
  const [dataSource, setDataSource] = useState(data);
  const [search, setSearch] = useState(searchObject || {});
  const [isLoading, setIsLoading] = useState(!!apiMethod);
  const [searchValue, setSearchValue] = useState(initialSearchTerm);
  const [rawApiData, setRawApiData] = useState([]);
  const searchInput = useRef(null);
  const prevSearchObjectRef = useRef(searchObject);
  const prevInitialSearchTermRef = useRef(null); // Initialize as null to detect first mount
  const isTypingRef = useRef(false); // Track if search change came from typing

  // Initialize search from URL on mount or when it changes externally
  useEffect(() => {
    // Only update if initialSearchTerm actually changed from previous value
    if (prevInitialSearchTermRef.current === initialSearchTerm) {
      return;
    }

    // If change came from typing, skip this effect (updateSearch already handled it)
    if (isTypingRef.current) {
      prevInitialSearchTermRef.current = initialSearchTerm;
      isTypingRef.current = false;
      return;
    }

    prevInitialSearchTermRef.current = initialSearchTerm;

    // Update the input value
    setSearchValue(initialSearchTerm);

    // Update the search query directly without calling updateSearch
    // to avoid circular updates
    if (!searchField) {
      return;
    }

    if (!initialSearchTerm || initialSearchTerm.trim() === "") {
      setSearch({});
      return;
    }

    const newSearchObject = {};
    const parts = searchField.split(" ");

    if (parts.length > 1) {
      newSearchObject.$or = [];
      for (const part of parts) {
        newSearchObject.$or.push({ [part]: initialSearchTerm });
      }
    } else {
      newSearchObject[searchField] = initialSearchTerm;
    }

    setSearch({ ...newSearchObject });
  }, [initialSearchTerm, searchField, searchValue]);

  // Add this effect to update search state when searchObject prop changes
  useEffect(() => {
    // Only update if searchObject exists and is different from previous
    const currentSearchObjectStr = JSON.stringify(searchObject || {});
    const prevSearchObjectStr = JSON.stringify(prevSearchObjectRef.current);

    if (currentSearchObjectStr !== prevSearchObjectStr) {
      prevSearchObjectRef.current = searchObject;
      setSearch(searchObject || {});
    }
  }, [searchObject]);

  // Data controller for API interactions
  const controller = useDataController({
    apiMethod,
    page,
    search,
    sort: activeSort,
    minSearchTriggerTime,
    refreshKey,
    itemsPerPage,
    requestFields,
    onDataRequested: () => {
      setIsLoading(true);
    },
    onData: (payload) => {
      let data = payload[apiDataField];
      const total = payload.total || payload.totalItems || 0;

      setIsLoading(false); // Reset loading state regardless of data
      if (onDataLoaded && data) data = onDataLoaded(data);

      if (!data) return;

      // Store raw API data for folding logic
      setRawApiData([...data]);

      // Apply folding logic if provided
      if (foldingLogic && foldingLogic.processData) {
        const processedData = foldingLogic.processData(data, searchValue);
        setDataSource(processedData);
      } else {
        setDataSource([...data]);
      }

      // Use original data length for pagination (not processed data)
      setTotalPages(Math.ceil(total / itemsPerPage));
      setTotalItems(total);
    },
  });

  // Handle data changes and client-side filtering
  useEffect(() => {
    if (!data) return;
    if (data) {
      // Apply client-side filtering if searchValue exists and no API method
      let filteredData = data;

      if (!apiMethod && searchValue && searchValue.trim() !== "" && searchField) {
        const searchTerm = searchValue.toLowerCase();
        const searchFields = searchField.split(" ");

        filteredData = data.filter(item => {
          return searchFields.some(field => {
            const value = extractValue(item, field);
            return value && String(value).toLowerCase().includes(searchTerm);
          });
        });
      }

      setDataSource(filteredData);
      setIsLoading(false);
      // Set pagination info for static data
      setTotalItems(filteredData.length);
      setTotalPages(Math.ceil(filteredData.length / itemsPerPage));
    }
  }, [data, itemsPerPage, searchValue, searchField, apiMethod]);

  // Handle folding logic changes
  useEffect(() => {
    if (foldingLogic && foldingLogic.processData && rawApiData.length > 0) {
      const processedData = foldingLogic.processData(rawApiData, searchValue);
      setDataSource(processedData);
    }
  }, [
    foldingLogic?.expandedGroups,
    foldingLogic?.collapsedGroups,
    rawApiData,
    foldingLogic,
    searchValue,
  ]);

  // Search handling
  const updateSearch = (value) => {
    setSearchValue(value);

    // Mark that this search change came from typing
    isTypingRef.current = true;

    // Notify parent component of search term change
    if (onSearchTermChange) {
      onSearchTermChange(value);
    }

    if (!searchField) {
      return;
    }

    // Handle empty search case
    if (!value || value.trim() === "") {
      setSearch({});
      return;
    }

    // Create a new search object to avoid modifying the original
    const newSearchObject = { ...searchObject };

    // Handle multi-field search
    const parts = searchField.split(" ");
    if (parts.length > 1) {
      // MongoDB $or requires array format, not object format
      newSearchObject.$or = [];
      for (const part of parts) {
        newSearchObject.$or.push({ [part]: value });
      }
    } else {
      // Single field search
      newSearchObject[searchField] = value;
    }

    // Update search state with the new object - force new reference
    setSearch({ ...newSearchObject });
  };

  // Selection handlers (defined before augmentedRenders to avoid hoisting issues)
  const handleSelectItem = useCallback((item, isSelected) => {
    const itemId = item[itemIdField];

    if (isControlled) {
      // Controlled mode - notify parent
      const newSelectedItems = isSelected
        ? [...selectedItems, itemId]
        : selectedItems.filter(id => id !== itemId);

      const newSelectedData = isSelected
        ? [...activeSelectedData, item]
        : activeSelectedData.filter(d => d[itemIdField] !== itemId);

      onSelectionChange(newSelectedItems, newSelectedData);
    } else {
      // Uncontrolled mode - manage internally
      if (isSelected) {
        setInternalSelectedItems(prev => [...prev, itemId]);
        setInternalSelectedData(prev => [...prev, item]);
      } else {
        setInternalSelectedItems(prev => prev.filter(id => id !== itemId));
        setInternalSelectedData(prev => prev.filter(d => d[itemIdField] !== itemId));
      }
    }
  }, [isControlled, selectedItems, activeSelectedData, itemIdField, onSelectionChange]);

  const handleSelectAll = useCallback((items, isSelected) => {
    if (isControlled) {
      const newSelectedItems = isSelected ? items.map(item => item[itemIdField]) : [];
      const newSelectedData = isSelected ? [...items] : [];
      onSelectionChange(newSelectedItems, newSelectedData);
    } else {
      if (isSelected) {
        setInternalSelectedItems(items.map(item => item[itemIdField]));
        setInternalSelectedData([...items]);
      } else {
        setInternalSelectedItems([]);
        setInternalSelectedData([]);
      }
    }
  }, [isControlled, itemIdField, onSelectionChange]);

  // Augment renders with selection column if enabled
  const augmentedRenders = useMemo(() => {
    if (!enableSelection) return renders;

    const selectionColumn = {
      id: "__selection__",
      headerClassName: "w-8",
      header: (_, items) => (
        <Checkbox
          isOn={items && items.length > 0 && activeSelectedItems.length === items.length}
          onChange={(isSelected) => handleSelectAll(items, isSelected)}
          variant={variant.includes('admin') ? 'admin_compact' : 'compact'}
        />
      ),
      render: (_, item) => (
        <Checkbox
          isOn={activeSelectedItems.includes(item[itemIdField])}
          onChange={(isSelected) => handleSelectItem(item, isSelected)}
          variant={variant.includes('admin') ? 'admin_compact' : 'compact'}
        />
      ),
    };

    // Insert selection column at the specified position
    if (selectionColumnPosition === "first") {
      return [selectionColumn, ...renders];
    } else if (selectionColumnPosition === "last") {
      return [...renders, selectionColumn];
    } else if (typeof selectionColumnPosition === "number") {
      const newRenders = [...renders];
      newRenders.splice(selectionColumnPosition, 0, selectionColumn);
      return newRenders;
    }

    return [selectionColumn, ...renders]; // Default to first
  }, [enableSelection, renders, activeSelectedItems, handleSelectAll, handleSelectItem, variant, itemIdField, selectionColumnPosition]);

  // Pagination calculations
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems || 0);
  const hasData = dataSource && dataSource.length > 0;

  const handleSortClick = (field) => {
    const newDirection = activeSortField === field && sortDirection === "asc" ? "desc" : "asc";
    setActiveSortField(field);
    setSortDirection(newDirection);
    setActiveSort({ field, order: newDirection });
  };

  // Item rendering
  const renderItems = () => {
    if (!hasData) return null;

    // Client-side pagination for static-data mode (apiMethod handles its own paging server-side).
    const pageData = apiMethod ? dataSource : dataSource.slice(startIndex, startIndex + itemsPerPage);

    // If custom renderRow function is provided, use it
    if (typeof renderRow === 'function') {
      return pageData.map((item, index) => renderRow(item, index));
    }

    // Default table row rendering
    return pageData.map((item, index) => (
      <tr
        key={
          item.type === "group"
            ? `group-${item.fullPath}`
            : `param-${item.param?.address || index}`
        }
        className={twMerge(
          styles.row,
          typeof styles.getRowStyle === "function"
            ? styles.getRowStyle(index, item)
            : "",
          itemHeight || ""
        )}
        onClick={() => {
          const clickHandler = onRowClick || styles.onRowClick;
          if (typeof clickHandler === "function") {
            clickHandler(item);
          }
        }}
      >
        {augmentedRenders.map((renderItem, subIndex) => (
          <td
            key={subIndex}
            className={twMerge(
              typeof styles.getCellStyle === "function"
                ? styles.getCellStyle(renderItem.itemClassName)
                : styles.cell
            )}
          >
            {renderItem.render
              ? renderItem.render(extractValue(item, renderItem.id), item)
              : (() => {
                  const value = extractValue(item, renderItem.id);
                  // Safety check: if value is an object, try to extract a renderable property
                  if (value && typeof value === 'object' && value !== null) {
                    // Try common object properties that might contain displayable text
                    return value.name || value.label || value.value || value.title || value.id || JSON.stringify(value);
                  }
                  return value;
                })()}
          </td>
        ))}
      </tr>
    ));
  };

  // Event handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (value) => {
    const newValue = parseInt(value);
    setItemsPerPage(newValue);
    setPage(1);

    // Save to local user preferences if key is provided
    if (preferenceKeys.itemsPerPage) {
      try {
        LocalUserPreferences.set(preferenceKeys.itemsPerPage, newValue);
      } catch (error) {
        console.warn("Failed to save itemsPerPage preference:", error);
      }
    }
  };

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        {title && <Typography className={styles.title}>{title}</Typography>}

        {searchField && showSearch && (
          <div className={styles.searchInput}>
            <Input
              variant={variant.includes('admin') ? 'admin' : 'default'}
              value={searchValue}
              onChange={(e) => updateSearch(e.target.value)}
              placeholder="Search..."
              className="w-full"
              icon={
                <MagnifyingGlassIcon className="h-5 w-5 text-admin_text_secondary" />
              }
              showClearButton={searchValue.length > 0}
              onClear={() => updateSearch("")}
            />
          </div>
        )}

        {filterControl && (
          <div className={styles.filterControl}>{filterControl}</div>
        )}

        {headerControls && (
          <div className={styles.headerControls}>{headerControls}</div>
        )}
      </div>

      {/* New section for tableHeader content */}
      {tableHeader && <div className="mb-4">{tableHeader}</div>}

      {/* Loading State */}
      {isLoading && onDataLoadingElement ? (
        <div className={styles.noDataContainer}>{onDataLoadingElement}</div>
      ) : hasData ? (
        <>
          {/* If custom renderRow is provided, skip table structure */}
          {typeof renderRow === 'function' ? (
            <div
              className={styles.tableWrapper}
              style={{ height: preferredHeight }}
              onMouseEnter={() => setIsScrollLocked(true)}
              onMouseLeave={() => setIsScrollLocked(false)}
            >
              <div className={styles.tbody}>
                {renderItems()}
              </div>
            </div>
          ) : (
            <div
              className={styles.tableWrapper}
              style={{ height: preferredHeight }}
              onMouseEnter={() => setIsScrollLocked(true)}
              onMouseLeave={() => setIsScrollLocked(false)}
            >
              <table className={styles.table}>
                <thead className={styles.thead}>
                  <tr className={styles.headerRow}>
                    {augmentedRenders.map((renderItem, index) => (
                      <th
                        key={index}
                        className={twMerge(
                          typeof styles.getHeaderCellStyle === "function"
                            ? styles.getHeaderCellStyle(renderItem.headerClassName)
                            : styles.headerCell,
                          renderItem.sortable ? "cursor-pointer select-none" : ""
                        )}
                        onClick={renderItem.sortable ? () => handleSortClick(renderItem.id) : undefined}
                      >
                        <div className="flex items-center gap-1">
                          {renderItem.header
                            ? typeof renderItem.header === "function"
                              ? renderItem.header(null, dataSource)
                              : renderItem.header
                            : renderItem.title}
                          {renderItem.sortable && (
                            activeSortField === renderItem.id
                              ? sortDirection === "asc"
                                ? <ChevronUpIcon className="w-3.5 h-3.5 shrink-0" />
                                : <ChevronDownIcon className="w-3.5 h-3.5 shrink-0" />
                              : <ChevronUpIcon className="w-3.5 h-3.5 shrink-0 opacity-30" />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>{renderItems()}</tbody>
              </table>
            </div>
          )}

          {/* Footer Section */}
          {(showFooter || tableFooter || (enableSelection && activeSelectedItems.length > 0 && bulkActions.length > 0)) && (
            <div className={styles.footer}>
              {/* Selection Footer - only show if bulkActions are provided */}
              {enableSelection && activeSelectedItems.length > 0 && bulkActions.length > 0 && (
                <div className="flex items-center justify-between p-4 border-t border-admin_bordered">
                  <Typography variant={variant.includes('admin') ? 'admin_body' : 'body'}>
                    {activeSelectedItems.length} item{activeSelectedItems.length !== 1 ? 's' : ''} selected
                  </Typography>
                  <div className="flex items-center gap-3">
                    <Button
                      variant={variant.includes('admin') ? 'admin_secondary' : 'secondary'}
                      onClick={() => {
                        if (isControlled) {
                          onSelectionChange([], []);
                        } else {
                          setInternalSelectedItems([]);
                          setInternalSelectedData([]);
                        }
                      }}
                    >
                      Deselect
                    </Button>
                    {bulkActions.map((action, index) => (
                      <Button
                        key={index}
                        variant={action.variant || (variant.includes('admin') ? 'admin_primary' : 'primary')}
                        onClick={() => action.onClick(activeSelectedItems, activeSelectedData)}
                      >
                        {action.icon && <span className="mr-1">{action.icon}</span>}
                        {action.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Table Footer Content - above pagination */}
              {tableFooter && <div className="mb-4">{tableFooter}</div>}

              {/* Pagination Controls */}
              {showFooter && (
                <PaginationControls
                  page={page}
                  totalPages={totalPages}
                  totalItems={totalItems || 0}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPage={itemsPerPage}
                  startIndex={startIndex}
                  endIndex={endIndex}
                />
              )}
            </div>
          )}
        </>
      ) : (
        // No Data Section
        <div className={styles.noDataContainer}>
          {noData || (
            <Typography variant="h3" className={styles.noDataText}>
              No data available
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

export default DataTable;
